/* eslint-disable eslint-comments/no-restricted-disable */

import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import ProjectDirectoryLayoutQuery from '@jira/global-pages__directories-projects-directory-v3/src/ui/page-layout/__generated__/ProjectDirectoryLayoutQuery$parameters';
import type { JiraProjectFilterInput } from '@jira/global-pages__directories-projects-directory-v3/src/ui/page-layout/__generated__/ProjectDirectoryRefetchQuery.graphql';
import type { ProjectDirectoryLayoutQuery as ProjectDirectoryLayoutQueryType } from '@jira/global-pages__directories-projects-directory-v3/src/ui/page-layout/__generated__/ProjectDirectoryLayoutQuery.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { AnyAri } from '@atlassian/ari/any-ari';
import { JiraProjectCategoryAri } from '@atlassian/ari/jira/project-category';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { Query } from '@atlassian/react-resource-router';

const DEFAULT_LIMIT = 25;
const PROJECTS_DIRECTORY_MANAGE = 'projects-directory-manage';

const DefaultFilterFields = {
	// sorting
	SortOrder: 'sortOrder',
	SortKey: 'sortKey',
	// pagination
	Page: 'page',
};

// TODO: go/restrict-enums
// eslint-disable-next-line no-restricted-syntax
enum TableColumns {
	Favourites = 'favourites',
	Key = 'key',
	Name = 'name',
	Type = 'type',
	Lead = 'lead',
	Category = 'category',
	LastIssueUpdate = 'last_issue_updated_time',
	Url = 'url',
	Action = 'actions',
}

const SortableColumns = [
	TableColumns.Key,
	TableColumns.Name,
	TableColumns.Lead,
	TableColumns.Category,
	TableColumns.LastIssueUpdate,
];

const FilterFields = {
	Keyword: 'keyword',
	ProjectType: 'types',
	ProjectCategoryId: 'projectCategoryId',
	...DefaultFilterFields,
};

const filterAliasMap = {
	contains: FilterFields.Keyword,
	selectedProjectType: FilterFields.ProjectType,
	selectedCategory: FilterFields.ProjectCategoryId,
};

const filterInitialState = {
	[FilterFields.Keyword]: '',
	[FilterFields.ProjectType]: '',
	[FilterFields.ProjectCategoryId]: '',
	[FilterFields.SortOrder]: 'ASC',
	[FilterFields.SortKey]: TableColumns.Name,
	[FilterFields.Page]: '1',
};

type Dictionary = {
	[key: string]: string;
};

const LEGACY_ALL_VALUE = 'all';

const sanitizeLegacyQuery = (query: Query = {}) => ({
	...query,
	selectedProjectType:
		query.selectedProjectType === LEGACY_ALL_VALUE
			? filterInitialState[FilterFields.ProjectType]
			: query.selectedProjectType,
	selectedCategory:
		query.selectedCategory === LEGACY_ALL_VALUE
			? filterInitialState[FilterFields.ProjectCategoryId]
			: query.selectedCategory,
});

const matchAliases = <T extends Dictionary>(
	filterState: T,
	query: Query,
	aliases: { [alias: string]: keyof T },
): T =>
	Object.keys(query).reduce(
		(filter, key) => {
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const alias = aliases[key] as string;
			const queryValue = query[key];
			const hasInitialValue = filter[key] !== undefined;

			// override default filter value with query params
			if (hasInitialValue && queryValue) {
				/* oxlint-disable typescript/consistent-type-assertions */
				// oxlint-disable-next-line no-param-reassign
				(filter as Dictionary)[key] = queryValue;
				/* oxlint-enable typescript/consistent-type-assertions */
				return filter;
			}

			// if there's no alias and no query param for it return default filter
			if (!alias || !queryValue) {
				return filter;
			}

			/* oxlint-disable typescript/consistent-type-assertions */
			// oxlint-disable-next-line no-param-reassign
			(filter as Dictionary)[alias] = queryValue;
			/* oxlint-enable typescript/consistent-type-assertions */
			return filter;
		},
		{ ...filterState },
	);

const CORE_PROJECT = 'business' as const;
const SOFTWARE_PROJECT = 'software' as const;
const SERVICE_DESK_PROJECT = 'service_desk' as const;
const PRODUCT_DISCOVERY_PROJECT = 'product_discovery' as const;

const PROJECT_TYPES: ProjectType[] = [
	CORE_PROJECT,
	SOFTWARE_PROJECT,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
];

const mapQueryToFilter = <T extends Dictionary>(
	filter: T,
	{
		cloudId,
		query,
		aliases,
	}: { cloudId: string; query?: Query; aliases?: { [alias: string]: keyof T } },
) => {
	const variables: JiraProjectFilterInput = {};
	const {
		[FilterFields.Keyword]: keyword,
		[FilterFields.ProjectCategoryId]: projectCategoryId,
		[FilterFields.ProjectType]: types,
		[FilterFields.SortOrder]: sortOrder,
		[FilterFields.SortKey]: sortKey,
	} = aliases && query ? matchAliases(filter, query, aliases) : filter;

	if (keyword) {
		variables.keyword = keyword;
	}

	if (projectCategoryId) {
		const isAlreadyAri = AnyAri.check(projectCategoryId);
		if (isAlreadyAri) {
			variables.projectCategoryId = projectCategoryId;
		} else {
			variables.projectCategoryId = JiraProjectCategoryAri.create({
				projectCategoryId,
				siteId: cloudId,
			}).toString();
		}
	}

	if (sortKey || sortOrder) {
		const sortByValue =
			// oxlint-disable-next-line typescript/consistent-type-assertions
			sortKey && SortableColumns.includes(sortKey as TableColumns)
				? sortKey
				: filterInitialState[FilterFields.SortKey];
		// oxlint-disable-next-line typescript/consistent-type-assertions
		variables.sortBy = {
			order: ['ASC', 'DESC'].includes(sortOrder)
				? sortOrder
				: filterInitialState[FilterFields.SortOrder],
			sortBy: sortByValue.toUpperCase(),
		} as JiraProjectFilterInput['sortBy'];
	}

	if (types && typeof types === 'string') {
		// oxlint-disable-next-line typescript/consistent-type-assertions
		variables.types = types
			.split(',') // oxlint-disable-next-line typescript/consistent-type-assertions
			.filter((type) => PROJECT_TYPES.includes(type as ProjectType))
			.map((type) => type.toUpperCase()) as unknown as JiraProjectFilterInput['types'];
	}
	return variables;
};

type ProjectsDirectoryEntryPointComponent = EntryPointComponent<
	{
		projectsDirectoryPage: ProjectDirectoryLayoutQueryType;
	},
	{},
	{},
	{
		isAdminProjectDirectoryRoute: boolean;
	}
>;

export const projectsDirectoryEntryPoint: EntryPoint<
	ProjectsDirectoryEntryPointComponent,
	EntryPointRouteParams
> = createEntryPoint({
	root: JSResourceForUserVisible<ProjectsDirectoryEntryPointComponent>(
		/* webpackChunkName: "async-projects-directory-v3", jiraSpaEntry: "async-projects-directory-v3" */ './ProjectsV3.tsx',
		{
			entryFsSsrLiftImportToModule: true,
		},
	),
	getPreloadProps({ context: { query, route }, tenantContext }: EntryPointRouteParams) {
		const { cloudId, isAnonymous } = tenantContext;
		const extraProps = {
			isAdminProjectDirectoryRoute: route.name === PROJECTS_DIRECTORY_MANAGE,
		};
		if (__SERVER__ && isAnonymous) {
			return { extraProps };
		}
		return {
			queries: {
				projectsDirectoryPage: {
					options: {
						fetchPolicy: 'network-only',
					},
					parameters: ProjectDirectoryLayoutQuery,
					variables: {
						cloudId: tenantContext.cloudId,
						filter: mapQueryToFilter(filterInitialState, {
							cloudId,
							query: sanitizeLegacyQuery(query),
							aliases: filterAliasMap,
						}),
						first: query?.page ? DEFAULT_LIMIT * (parseInt(query.page, 10) || 1) : DEFAULT_LIMIT,
						last: DEFAULT_LIMIT,
						isAdminSettingsContext: route.name === PROJECTS_DIRECTORY_MANAGE,
						isAnonymous,
						includeCsmLandingModal: fg('csm_landing_modal'),
					},
				},
			},
			extraProps,
		};
	},
});
